import React, { ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { Button, Text, Popup, IconButton } from 'components'
import { ButtonVariant } from 'components/Buttons/Button'
import Divider from '../Divider'
import { MODAL_SIZE } from './constants'

type Size = keyof typeof MODAL_SIZE
export type Props = {
  size?: Size
  children?: ReactNode
  title?: ReactNode
  footer?: ReactNode
  okText?: string
  cancelText?: string
  visible: boolean
  header?: ReactNode
  onClose?: () => void
  onOk?: () => void
  isSubmitting?: boolean
  contentVariant?: 'confirmation' | 'normal'
  hideOk?: boolean
  disableOk?: boolean
  okButtonVariant?: ButtonVariant
  customSize?: {
    maxWidth: string
    maxHeight: string
  }
  minWidth?: string
  minHeight?: string
  isFooterVisible?: boolean
  styleOverrides?: Record<'modalContent', React.CSSProperties>
  utils?: ReactNode
  fullHeight?: boolean
}

const Modal: React.FC<Props> = (props) => {
  const {
    size = 's',
    children,
    title,
    cancelText,
    okText,
    visible,
    onClose,
    onOk,
    header,
    footer,
    isSubmitting,
    contentVariant = 'confirmation',
    disableOk = false,
    hideOk = false,
    okButtonVariant = 'fill',
    customSize,
    isFooterVisible = true,
    minWidth = '580px',
    minHeight = '230px',
    styleOverrides,
    utils,
    fullHeight = false,
  } = props

  const { maxWidth, maxHeight } = customSize ? customSize : MODAL_SIZE[size]
  const modalTitle =
    !!title && typeof title === 'string' ? (
      <Text fontWeight="bold" fontSize={'16px'}>
        {title}
      </Text>
    ) : (
      title
    )

  const withHeader = !!title || !!header

  return visible ? (
    <Popup minWidth={minWidth} width="auto" fullHeight={fullHeight}>
      <ModalContainer maxWidth={maxWidth} maxHeight={maxHeight}>
        {withHeader && (
          <>
            <ModalHeader>
              <span>{header || modalTitle}</span>
              <UtilsContainer>
                {utils ? utils : null}
                <CloseButton variant="ghost" icon="times" onClick={onClose} />
              </UtilsContainer>
            </ModalHeader>

            <Divider />
          </>
        )}

        <ModalContent contentVariant={contentVariant} minHeight={minHeight} style={styleOverrides?.modalContent || {}}>
          {children}
        </ModalContent>
        {isFooterVisible && (
          <>
            <Divider />
            <ModalFooter okOnly={!Boolean(onClose)}>
              {footer || (
                <>
                  <Button
                    onMouseDown={(e) => e.stopPropagation()}
                    variant="ghost"
                    onClick={onClose}
                    disabled={isSubmitting}
                  >
                    {cancelText}
                  </Button>
                  {!hideOk && onOk && (
                    <Button
                      disabled={disableOk}
                      isSubmitting={isSubmitting}
                      onClick={onOk}
                      variant={okButtonVariant}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      {okText}
                    </Button>
                  )}
                </>
              )}
            </ModalFooter>
          </>
        )}
      </ModalContainer>
    </Popup>
  ) : null
}

export default Modal

const CloseButton = styled((props) => <IconButton {...props} />)`
  & svg {
    color: ${(p) => p.theme.colors.gray5};
  }
`

const ModalContainer = styled.div<{ maxWidth: string; maxHeight: string }>`
  box-sizing: border-box;
  max-width: ${(p) => p.maxWidth};
  max-height: ${(p) => p.maxHeight};
  width: 100%;
  height: 100%;
  border-radius: 4px;
`

const ModalHeader = styled.div`
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 1rem;
`

const UtilsContainer = styled.div`
  display: flex;
  gap: 10px;
  line-height: 20px;
  align-items: center;
  justify-content: flex-end;
`

const ModalFooter = styled.div<{ okOnly?: boolean }>`
  padding: 1rem;
  box-sizing: border-box;
  display: flex;
  justify-content: ${(p) => (p.okOnly ? 'flex-end' : 'space-between')};
  align-items: center;
  height: 64px;
  gap: 1rem;
`
const CenterContentCss = css`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`

const ModalContent = styled.div<{
  contentVariant?: 'confirmation' | 'normal'
  minHeight: string
}>`
  min-height: ${(p) => p.minHeight};
  box-sizing: border-box;
  ${(p) => (p.contentVariant === 'confirmation' ? CenterContentCss : '')}
`
